import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import { useMemo, useState, useRef } from "react";
import {
  DetectionSegment,
  useProductDetectionsBySegment,
} from "../MarketLandscape/hooks/useProductDetectionsBySegment";
import { convertProductDetectionData } from "../MarketLandscape/utils";
import { getTop5Products } from "./utils";
import useMatchProductsById from "../MarketLandscape/hooks/useMatchProductsById";
import { ChartWrapper } from "../../../../components/ChartWrapper";
import { ClusteredColumnChart } from "../../../../components/charts/ClusteredColumnChart/ClusteredColumnChart";
import { BarChartColors } from "../../../../components/charts/constants";
import { PeriodSelect } from "../../../../components/filters/PeriodSelect";
import { useWorkspaceStore } from "../../../../store/workspace/WorkspaceStore";
import {
  determineChartContent,
  generateChartSegmentNamesForCSV,
  hasNoData,
} from "../../utils";
import { useCombinedFilters } from "../../../../hooks/useCombinedFilters";
import * as am4charts from "@amcharts/amcharts4/charts";
import { CHART_CONSTANTS } from "../constants";
import { useCountryNameMap } from "../../../../hooks/useCountryNameMap";

const { baseId, tooltip, chartTitle } =
  CHART_CONSTANTS.competitorNewProductDetectionsBySegment;

export const CompetitorNewProductDetectionsBySegment = () => {
  const [selectedPeriod, setSelectedPeriod] = useState("1");
  const [selectedSegment, setSelectedSegment] = useState(
    DetectionSegment.COUNTRY
  );
  const chartRef = useRef<am4charts.XYChart>(null);
  const { countryNameMap } = useCountryNameMap();
  const combinedFilters = useCombinedFilters();
  const competitorsProductIds = useWorkspaceStore(
    (state) => state.draft?.configuration.competitorsProductIds || []
  );
  const localizeInstalls = useWorkspaceStore(
    (state) => state.draft?.configuration?.localizeInstalls ?? true
  );

  const {
    isLoading: isDetectionDataLoading,
    productDetectionsBySegmentBreakdown,
  } = useProductDetectionsBySegment({
    productIds: competitorsProductIds,
    companyFilters: combinedFilters || {},
    localizeInstalls,
    lookBackYears: Number.parseInt(selectedPeriod),
    segment: selectedSegment,
  });

  const { productsNameMapById } = useMatchProductsById(competitorsProductIds);

  const chartData = useMemo(
    () =>
      convertProductDetectionData({
        productDetectionData: getTop5Products(
          productDetectionsBySegmentBreakdown
        ),
        productNameInfo: productsNameMapById,
        segment: selectedSegment,
        countryNameMap,
      }),
    [
      productDetectionsBySegmentBreakdown,
      productsNameMapById,
      selectedSegment,
      countryNameMap,
    ]
  );

  const noDataReturned = useMemo(
    () => hasNoData(isDetectionDataLoading, chartData),
    [chartData, isDetectionDataLoading]
  );

  const chartContent = useMemo(() => {
    return determineChartContent({
      productIds: competitorsProductIds,
      isLoading: isDetectionDataLoading,
      noDataReturned,
      baseId,
      chartToRender: (
        <ClusteredColumnChart
          id={`${baseId}-chart`}
          chartData={chartData}
          baseColor={BarChartColors}
          yAxisLabel="New Product Detections"
          ref={chartRef}
        />
      ),
    });
  }, [
    chartData,
    isDetectionDataLoading,
    noDataReturned,
    competitorsProductIds,
  ]);

  const csvFieldMappings = useMemo(() => {
    const fieldMappings: Record<string, string> = {
      category: generateChartSegmentNamesForCSV(selectedSegment),
    };

    if (chartData.length > 0) {
      const uniqueProducts = new Set<string>();
      chartData.forEach((item) => {
        item.cluster.forEach((product) => {
          uniqueProducts.add(product.name);
        });
      });

      uniqueProducts.forEach((productName) => {
        fieldMappings[productName] = `${productName} (New Product Detections)`;
        fieldMappings["totalCompanyCount"] = `${productName} (Total Companies)`;
      });
    }

    return fieldMappings;
  }, [chartData, selectedSegment]);

  function handleSegmentSelect(event: SelectChangeEvent) {
    setSelectedSegment(event.target.value as DetectionSegment);
  }

  return (
    <ChartWrapper
      id={`${baseId}-chart-wrapper`}
      title={chartTitle}
      tooltipTitle={tooltip}
      chart={chartContent}
      chartRef={chartRef}
      noData={noDataReturned || competitorsProductIds.length === 0}
      isLoading={isDetectionDataLoading}
      titleExtras={
        <Box
          display="flex"
          flexDirection="row"
          gap="5px"
          flexWrap="wrap"
          minWidth={0}
        >
          <PeriodSelect
            id={`${baseId}-period-select`}
            selectedPeriod={selectedPeriod}
            onChange={setSelectedPeriod}
          />
          <FormControl size="small" sx={{ minWidth: 180, maxWidth: "100%" }}>
            <Select
              id={`${baseId}-select`}
              value={selectedSegment}
              onChange={handleSegmentSelect}
            >
              <MenuItem value={`${DetectionSegment.COUNTRY}`}>
                Segment: Country
              </MenuItem>
              <MenuItem value={`${DetectionSegment.REGION}`}>
                Segment: Region
              </MenuItem>
              <MenuItem value={`${DetectionSegment.INDUSTRY}`}>
                Segment: HG Industry
              </MenuItem>
              <MenuItem value={`${DetectionSegment.INDUSTRY_NAICS}`}>
                Segment: NAICS
              </MenuItem>
              <MenuItem value={`${DetectionSegment.EMPLOYEE}`}>
                Segment: Employee Range
              </MenuItem>
              <MenuItem value={`${DetectionSegment.REVENUE}`}>
                Segment: Revenue Range
              </MenuItem>
            </Select>
          </FormControl>
        </Box>
      }
      csvFieldMappings={csvFieldMappings}
    />
  );
};
